import * as SentryBrowser from '@sentry/browser';
import { isSentryEnabled } from '../tools/sentry/sentry';

// Re-export reportToSentry directly from @sonnen/web-utils
export { reportToSentry, type SentryReportOptions } from '@sonnen/web-utils';

export type SentryLevel = 'fatal' | 'error' | 'warning' | 'info' | 'debug';

/**
 * Sentry utilities for customer-dynamic-tariff-frontend.
 *
 * Core functionality (reportToSentry) is re-exported from @sonnen/web-utils.
 *
 * Additional utilities for debugging and context:
 * - reportException: For catching and reporting errors
 * - setUserContext/clearUserContext: GDPR-compliant user tracking (ID only)
 * - addBreadcrumb: Track user actions for debugging
 * - setContext: Track feature flags, app state, etc.
 *
 * GDPR Compliance:
 * - Only user IDs are sent (no email, name, or PII)
 * - Use internal identifiers only
 */

/**
 * Report an exception to Sentry.
 * Use this in catch blocks for actual errors that need tracking.
 *
 * @param error - The error to report
 * @param options - Optional context (tag, context)
 *
 * @example
 * try {
 *   await saveData();
 * } catch (error) {
 *   reportException(error, {
 *     tag: 'storage',
 *     context: { operation: 'save' }
 *   });
 * }
 */
export const reportException = (
  error: Error | unknown,
  options: {
    tag?: string;
    context?: Record<string, unknown>;
  } = {},
): void => {
  if (!isSentryEnabled()) {
    return;
  }

  const { tag, context } = options;

  SentryBrowser.withScope((scope) => {
    if (tag) {
      scope.setTag('category', tag);
    }

    if (context) {
      scope.setContext('error_details', {
        ...context,
        timestamp: new Date().toISOString(),
      });
    }

    SentryBrowser.captureException(error);
  });
};

/**
 * Set user context for Sentry events - GDPR compliant.
 * Only accepts userId (internal identifier) - no PII.
 *
 * ⚠️ GDPR/Privacy: Never pass email, name, or other personally identifiable information.
 *
 * @param userId - Internal user ID (non-PII identifier)
 *
 * @example
 * setUserContext(user.id);
 */
export const setUserContext = (userId: string): void => {
  if (!isSentryEnabled()) {
    return;
  }

  SentryBrowser.setUser({ id: userId });
};

/**
 * Clear user context from Sentry.
 * Call this on logout to stop associating events with the user.
 *
 * @example
 * clearUserContext();
 */
export const clearUserContext = (): void => {
  if (!isSentryEnabled()) {
    return;
  }

  SentryBrowser.setUser(null);
};

/**
 * Add a breadcrumb for debugging context.
 * Breadcrumbs show the sequence of events leading to an error.
 * Very useful for understanding what happened before an error occurred.
 *
 * @param message - Breadcrumb message describing what happened
 * @param category - Category for grouping (e.g., 'navigation', 'ui', 'api')
 * @param level - Severity level
 * @param data - Additional context (no PII!)
 *
 * @example
 * // Track navigation
 * addBreadcrumb('User navigated to checkout', 'navigation', 'info', {
 *   from: '/cart',
 *   to: '/checkout'
 * });
 *
 * @example
 * // Track API call
 * addBreadcrumb('Fetching user data', 'api', 'info', {
 *   endpoint: '/api/user',
 *   method: 'GET'
 * });
 */
export const addBreadcrumb = (
  message: string,
  category?: string,
  level: SentryLevel = 'info',
  data?: Record<string, unknown>,
): void => {
  if (!isSentryEnabled()) {
    return;
  }

  SentryBrowser.addBreadcrumb({
    message,
    category,
    level,
    data,
    timestamp: Date.now() / 1000,
  });
};

/**
 * Set custom context for Sentry events.
 * Useful for tracking feature flags, app state, or other non-PII context.
 *
 * ⚠️ GDPR/Privacy: Never include PII in context values.
 *
 * @param key - Context key (e.g., 'feature_flags', 'app_state')
 * @param value - Context data (no PII!)
 *
 * @example
 * // Track feature flags
 * setContext('feature_flags', {
 *   newCheckout: true,
 *   betaUI: false
 * });
 *
 * @example
 * // Track app state
 * setContext('app_state', {
 *   currentFlow: 'registration',
 *   step: 3
 * });
 */
export const setContext = (key: string, value: Record<string, unknown> | null): void => {
  if (!isSentryEnabled()) {
    return;
  }

  SentryBrowser.setContext(key, value);
};
